import React, { useState, useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import { API_ENDPOINTS } from '../config/api-endpoints';
import LineChart from '../components/charts/LineChart';
import BarChart from '../components/charts/BarChart';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import './Monitoring.css';

const { Skeleton } = SkeletonLoaderComponents;

const Monitoring = () => {
  const { user } = useAdminAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);
  const fetchMetricsRef = useRef(null);

  // Configurable thresholds
  const [thresholds, setThresholds] = useState({
    errorRate: 5, // percentage
    latency: 1000, // milliseconds
    throttleRate: 1 // percentage
  });

  const fetchMetrics = async () => {
    try {
      setError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_ENDPOINTS.ADMIN.SYSTEM_MONITOR}/metrics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }

      const data = await response.json();
      setMetrics(data);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  fetchMetricsRef.current = fetchMetrics;

  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    if (isAutoRefresh && refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchMetricsRef.current();
      }, refreshInterval * 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isAutoRefresh, refreshInterval]);

  const handleRefreshIntervalChange = (seconds) => {
    setRefreshInterval(seconds);
  };

  const handleManualRefresh = () => {
    fetchMetrics();
  };

  const checkThreshold = (value, threshold, type = 'above') => {
    if (type === 'above') {
      return value > threshold;
    }
    return value < threshold;
  };

  if (loading && !metrics) {
    return (
      <div className="monitoring">
        <div className="monitoring__header">
          <h1>Real-time Monitoring</h1>
        </div>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} width="100%" height="120px" className="admin-skeleton-gap" />
        ))}
      </div>
    );
  }

  const activeUsers = metrics?.realtime?.activeUsers || 0;
  const apiRequestRate = metrics?.realtime?.apiRequestRate || 0;
  const errorRate = metrics?.realtime?.errorRate || 0;
  const avgLatency = metrics?.realtime?.avgLatency || 0;

  const hasErrorAlert = checkThreshold(errorRate, thresholds.errorRate);
  const hasLatencyAlert = checkThreshold(avgLatency, thresholds.latency);

  return (
    <div className="monitoring">
      <div className="monitoring__header">
        <h1>Real-time Monitoring</h1>
        <div className="monitoring__controls">
          <div className="monitoring__refresh-control">
            <label>
              <input
                type="checkbox"
                checked={isAutoRefresh}
                onChange={(e) => setIsAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            {isAutoRefresh && (
              <select
                value={refreshInterval}
                onChange={(e) => handleRefreshIntervalChange(Number(e.target.value))}
                className="monitoring__interval-select"
              >
                <option value={10}>10s</option>
                <option value={30}>30s</option>
                <option value={60}>1m</option>
                <option value={300}>5m</option>
              </select>
            )}
          </div>
          <button
            className="monitoring__refresh-btn"
            onClick={handleManualRefresh}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>

      {lastUpdated && (
        <div className="monitoring__last-updated">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      {error && (
        <div className="monitoring__alert monitoring__alert--error">
          {error}
        </div>
      )}

      {(hasErrorAlert || hasLatencyAlert) && (
        <div className="monitoring__alerts">
          {hasErrorAlert && (
            <div className="monitoring__alert monitoring__alert--warning">
              ⚠️ High error rate detected: {errorRate.toFixed(2)}% (threshold: {thresholds.errorRate}%)
            </div>
          )}
          {hasLatencyAlert && (
            <div className="monitoring__alert monitoring__alert--warning">
              ⚠️ High latency detected: {avgLatency.toFixed(0)}ms (threshold: {thresholds.latency}ms)
            </div>
          )}
        </div>
      )}

      {/* Real-time Metrics */}
      <section className="monitoring__section">
        <h2>Live Metrics</h2>
        <div className="monitoring__metrics-grid">
          <MetricCard
            title="Active Users"
            value={activeUsers}
            unit="users"
            icon="👥"
            trend={metrics?.realtime?.activeUsersTrend}
          />
          <MetricCard
            title="API Request Rate"
            value={apiRequestRate}
            unit="req/min"
            icon="📊"
            trend={metrics?.realtime?.requestRateTrend}
          />
          <MetricCard
            title="Error Rate"
            value={errorRate}
            unit="%"
            icon="⚠️"
            trend={metrics?.realtime?.errorRateTrend}
            alert={hasErrorAlert}
          />
          <MetricCard
            title="Avg Latency"
            value={avgLatency}
            unit="ms"
            icon="⏱️"
            trend={metrics?.realtime?.latencyTrend}
            alert={hasLatencyAlert}
          />
        </div>
      </section>

      {/* Lambda Functions Health */}
      <section className="monitoring__section">
        <h2>Lambda Functions</h2>
        <div className="monitoring__lambda-grid">
          {metrics?.lambda?.functions?.map((func) => (
            <LambdaCard
              key={func.name}
              function={func}
              thresholds={thresholds}
            />
          )) || (
            <div className="monitoring__empty">No Lambda function data available</div>
          )}
        </div>
      </section>

      {/* DynamoDB Metrics */}
      <section className="monitoring__section">
        <h2>DynamoDB Tables</h2>
        <div className="monitoring__dynamodb-grid">
          {metrics?.dynamodb?.tables?.map((table) => (
            <DynamoDBCard
              key={table.name}
              table={table}
              thresholds={thresholds}
            />
          )) || (
            <div className="monitoring__empty">No DynamoDB data available</div>
          )}
        </div>
      </section>

      {/* Charts */}
      <section className="monitoring__section">
        <h2>Historical Trends</h2>
        <div className="monitoring__charts">
          <div className="monitoring__chart">
            <h3>Request Rate (Last Hour)</h3>
            <LineChart
              data={{
                labels: metrics?.charts?.requestRate?.labels || [],
                datasets: [{
                  label: 'Requests/min',
                  data: metrics?.charts?.requestRate?.data || [],
                  borderColor: 'rgb(0, 102, 255)',
                  backgroundColor: 'rgba(0, 102, 255, 0.1)',
                  tension: 0.4
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }}
            />
          </div>

          <div className="monitoring__chart">
            <h3>Error Rate (Last Hour)</h3>
            <LineChart
              data={{
                labels: metrics?.charts?.errorRate?.labels || [],
                datasets: [{
                  label: 'Error %',
                  data: metrics?.charts?.errorRate?.data || [],
                  borderColor: 'rgb(255, 99, 132)',
                  backgroundColor: 'rgba(255, 99, 132, 0.1)',
                  tension: 0.4
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 10
                  }
                }
              }}
            />
          </div>

          <div className="monitoring__chart">
            <h3>Lambda Invocations by Function</h3>
            <BarChart
              data={{
                labels: metrics?.charts?.lambdaInvocations?.labels || [],
                datasets: [{
                  label: 'Invocations',
                  data: metrics?.charts?.lambdaInvocations?.data || [],
                  backgroundColor: 'rgba(0, 217, 181, 0.6)',
                  borderColor: 'rgb(0, 217, 181)',
                  borderWidth: 1
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }}
            />
          </div>
        </div>
      </section>

      {/* Threshold Configuration */}
      <section className="monitoring__section">
        <h2>Alert Thresholds</h2>
        <div className="monitoring__thresholds">
          <div className="monitoring__threshold-item">
            <label>Error Rate Threshold (%):</label>
            <input
              type="number"
              value={thresholds.errorRate}
              onChange={(e) => setThresholds({ ...thresholds, errorRate: Number(e.target.value) })}
              min="0"
              max="100"
            />
          </div>
          <div className="monitoring__threshold-item">
            <label>Latency Threshold (ms):</label>
            <input
              type="number"
              value={thresholds.latency}
              onChange={(e) => setThresholds({ ...thresholds, latency: Number(e.target.value) })}
              min="0"
              step="100"
            />
          </div>
          <div className="monitoring__threshold-item">
            <label>Throttle Rate Threshold (%):</label>
            <input
              type="number"
              value={thresholds.throttleRate}
              onChange={(e) => setThresholds({ ...thresholds, throttleRate: Number(e.target.value) })}
              min="0"
              max="100"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const MetricCard = ({ title, value, unit, icon, trend, alert }) => {
  const trendIcon = trend > 0 ? '↑' : trend < 0 ? '↓' : '→';
  const trendClass = trend > 0 ? 'positive' : trend < 0 ? 'negative' : 'neutral';

  return (
    <div className={`metric-card ${alert ? 'metric-card--alert' : ''}`}>
      <div className="metric-card__icon">{icon}</div>
      <div className="metric-card__content">
        <div className="metric-card__title">{title}</div>
        <div className="metric-card__value">
          {typeof value === 'number' ? value.toLocaleString() : value}
          <span className="metric-card__unit">{unit}</span>
        </div>
        {trend !== undefined && (
          <div className={`metric-card__trend metric-card__trend--${trendClass}`}>
            {trendIcon} {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
    </div>
  );
};

const LambdaCard = ({ function: func, thresholds }) => {
  const errorRate = func.errorCount / func.invocationCount * 100 || 0;
  const hasAlert = errorRate > thresholds.errorRate;

  return (
    <div className={`lambda-card ${hasAlert ? 'lambda-card--alert' : ''}`}>
      <h3 className="lambda-card__name">{func.name}</h3>
      <div className="lambda-card__metrics">
        <div className="lambda-card__metric">
          <span className="lambda-card__label">Invocations:</span>
          <span className="lambda-card__value">{func.invocationCount.toLocaleString()}</span>
        </div>
        <div className="lambda-card__metric">
          <span className="lambda-card__label">Errors:</span>
          <span className={`lambda-card__value ${hasAlert ? 'lambda-card__value--alert' : ''}`}>
            {func.errorCount} ({errorRate.toFixed(2)}%)
          </span>
        </div>
        <div className="lambda-card__metric">
          <span className="lambda-card__label">Avg Duration:</span>
          <span className="lambda-card__value">{func.avgDuration.toFixed(0)}ms</span>
        </div>
        <div className="lambda-card__metric">
          <span className="lambda-card__label">Throttles:</span>
          <span className="lambda-card__value">{func.throttleCount}</span>
        </div>
      </div>
    </div>
  );
};

const DynamoDBCard = ({ table, thresholds }) => {
  const readUtilization = (table.readCapacityUsed / table.readCapacityTotal * 100) || 0;
  const writeUtilization = (table.writeCapacityUsed / table.writeCapacityTotal * 100) || 0;
  const hasThrottling = table.throttledRequests > 0;

  return (
    <div className={`dynamodb-card ${hasThrottling ? 'dynamodb-card--alert' : ''}`}>
      <h3 className="dynamodb-card__name">{table.name}</h3>
      <div className="dynamodb-card__metrics">
        <div className="dynamodb-card__capacity">
          <div className="dynamodb-card__capacity-item">
            <span className="dynamodb-card__label">Read Capacity:</span>
            <div className="dynamodb-card__progress">
              <div
                className="dynamodb-card__progress-bar"
                style={{ width: `${Math.min(readUtilization, 100)}%` }}
              />
            </div>
            <span className="dynamodb-card__value">{readUtilization.toFixed(1)}%</span>
          </div>
          <div className="dynamodb-card__capacity-item">
            <span className="dynamodb-card__label">Write Capacity:</span>
            <div className="dynamodb-card__progress">
              <div
                className="dynamodb-card__progress-bar"
                style={{ width: `${Math.min(writeUtilization, 100)}%` }}
              />
            </div>
            <span className="dynamodb-card__value">{writeUtilization.toFixed(1)}%</span>
          </div>
        </div>
        {hasThrottling && (
          <div className="dynamodb-card__alert">
            ⚠️ {table.throttledRequests} throttled requests
          </div>
        )}
      </div>
    </div>
  );
};

export default Monitoring;
